import React, { useState } from 'react';
import { ui } from './ui';
import { TYPES, TYPE_META, blankQuestion, blankSection } from './paperUtils';
import { streamGenerate, errMsg } from './paperApi';
import PastePanel from './PastePanel';

const DEFAULT_MARKS = { mcq: 1, fill_blank: 1, true_false: 1, very_short: 1, short: 3, long: 5, match: 4, case_study: 4 };

// Mode A (AI generation, streamed) + Mode B (smart paste). Results are appended as sections.
export default function IngestPanel({ user, paper, setSections, aiConfigured, topics, setTopics, difficulty, setDifficulty }) {
  const [counts, setCounts] = useState({ mcq: 5, short: 3 });
  const [targetMarks, setTargetMarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const requests = TYPES.filter((t) => Number(counts[t]) > 0).map((t) => ({ type: t, count: Number(counts[t]), marks: DEFAULT_MARKS[t] }));
  const planned = requests.reduce((a, r) => a + r.count * r.marks, 0);

  const appendSection = (title, questions, instr = '') => setSections((ss) => {
    const sec = { ...blankSection(ss.length), sectionTitle: title, sectionInstructions: instr, questions };
    // Re-use a single empty starter section instead of leaving it blank.
    return ss.length === 1 && !ss[0].questions.length ? [{ ...sec, id: ss[0].id, sectionTitle: ss[0].sectionTitle }] : [...ss, sec];
  });

  const generate = async () => {
    setMsg({ type: '', text: '' });
    if (!paper.classId || !paper.subject) return setMsg({ type: 'err', text: 'Select class and subject in Setup first.' });
    if (!requests.length) return setMsg({ type: 'err', text: 'Enter a count for at least one question type.' });
    setBusy(true);
    let got = 0; const errors = [];
    try {
      await streamGenerate(user, { classId: paper.classId, subject: paper.subject, topics, difficulty, requests }, (ev) => {
        if (ev.event === 'batch') {
          const qs = ev.questions.map((q) => ({ ...blankQuestion(ev.type, q.marks), ...q }));
          got += qs.length;
          const m = requests[ev.index]?.marks || 1;
          appendSection(`Section ${String.fromCharCode(65 + ev.index)}: ${TYPE_META[ev.type]}`, qs, `Each question carries ${m} mark${m === 1 ? '' : 's'}.`);
          setMsg({ type: 'ok', text: `Generated ${got} question(s)…` });
        } else if (ev.event === 'error') errors.push(`${TYPE_META[ev.type]}: ${ev.error}`);
      });
      setMsg(errors.length ? { type: 'err', text: errors.join(' | ') } : { type: 'ok', text: `Done — ${got} question(s) added as new sections.` });
    } catch (e) { setMsg({ type: 'err', text: errMsg(e) }); }
    setBusy(false);
  };

  return (
    <div>
      <div style={ui.card}>
        <b>✨ Mode A — AI generation</b>
        {!aiConfigured && <div style={ui.err}>AI is not configured on the server. You can still use manual entry and Smart Paste.</div>}
        <span style={ui.label}>Chapters / topics (comma separated)</span>
        <input style={ui.input} value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="Light, Reflection of light, Human eye" />
        <div style={ui.grid2}>
          <div><span style={ui.label}>Difficulty</span>
            <select style={ui.input} value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option></select></div>
          <div><span style={ui.label}>Target marks (guide)</span>
            <input type="number" style={ui.input} value={targetMarks} onChange={(e) => setTargetMarks(e.target.value)} /></div>
        </div>
        <span style={ui.label}>Question counts</span>
        <div style={ui.grid2}>
          {TYPES.map((t) => (
            <label key={t} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
              {TYPE_META[t]} ({DEFAULT_MARKS[t]}m)
              <input type="number" min="0" max="30" style={{ ...ui.input, width: 60 }} value={counts[t] || ''} onChange={(e) => setCounts({ ...counts, [t]: e.target.value })} />
            </label>
          ))}
        </div>
        <div style={{ fontSize: 12, margin: '8px 0', color: targetMarks && Number(targetMarks) !== planned ? '#b45309' : '#475569' }}>
          Planned total: <b>{planned}</b> marks{targetMarks ? ` (target ${targetMarks})` : ''}
        </div>
        <button type="button" style={ui.btnPrimary} disabled={busy || !aiConfigured} onClick={generate}>{busy ? 'Generating…' : '✨ Generate with AI'}</button>
        {msg.text && <div style={msg.type === 'err' ? ui.err : ui.ok}>{msg.text}</div>}
      </div>
      <PastePanel onAdd={(qs) => appendSection('Pasted Questions', qs)} />
    </div>
  );
}
